import { useState } from "react";
import { Plus, Trash2, AlertCircle, CheckCircle2 } from "lucide-react";
import { useApp, fmt, genId } from "../../context/AppContext";

const CATEGORIES = [
  { value: "needs", label: "Needs", color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/20" },
  { value: "wants", label: "Wants", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
  { value: "savings", label: "Savings", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
];

const CAT_STYLE = { needs: "text-rose-400 bg-rose-500/10", wants: "text-amber-400 bg-amber-500/10", savings: "text-emerald-400 bg-emerald-500/10" };

function AddIncomeRow({ onAdd }) {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const submit = () => {
    if (!name || !amount) return;
    onAdd({ id: genId(), name, amount: parseFloat(amount) || 0 });
    setName(""); setAmount("");
  };
  return (
    <div className="flex gap-2 mt-3">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Source name" className="flex-1 bg-slate-900/80 border border-slate-800 focus:border-emerald-500/50 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 outline-none transition-colors" />
      <div className="relative w-32">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" className="w-full bg-slate-900/80 border border-slate-800 focus:border-emerald-500/50 rounded-lg pl-6 pr-3 py-2 text-sm text-white placeholder-slate-600 outline-none transition-colors" />
      </div>
      <button onClick={submit} data-testid="add-income-btn" className="px-3 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-400 rounded-lg transition-colors">
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}

function AddExpenseRow({ onAdd }) {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("needs");
  const [vital, setVital] = useState(true);
  const [variable, setVariable] = useState(false);
  const [dueDate, setDueDate] = useState("");

  const submit = () => {
    if (!name || !amount) return;
    onAdd({ id: genId(), name, amount: parseFloat(amount) || 0, category, vital, variable, dueDate: dueDate ? parseInt(dueDate) : null });
    setName(""); setAmount(""); setCategory("needs"); setVital(true); setVariable(false); setDueDate("");
  };

  return (
    <div className="bg-slate-900/60 border border-white/5 rounded-xl p-4 mt-3 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Bill name" className="col-span-2 bg-slate-950 border border-slate-800 focus:border-emerald-500/50 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 outline-none transition-colors" />
        <div className="relative">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500/50 rounded-lg pl-6 pr-3 py-2 text-sm text-white placeholder-slate-600 outline-none transition-colors" />
        </div>
        <input type="number" value={dueDate} onChange={(e) => setDueDate(e.target.value)} placeholder="Due (day)" min="1" max="31" className="bg-slate-950 border border-slate-800 focus:border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 outline-none transition-colors" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {CATEGORIES.map((c) => (
            <button key={c.value} onClick={() => setCategory(c.value)} className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors border ${
              category === c.value ? `${c.color} ${c.bg} ${c.border}` : "text-slate-500 border-transparent hover:text-slate-300"
            }`}>{c.label}</button>
          ))}
        </div>
        <button onClick={() => setVital(!vital)} data-testid="vital-toggle" className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium transition-colors border ${
          vital ? "text-sky-400 bg-sky-500/10 border-sky-500/20" : "text-slate-500 border-slate-700 hover:text-slate-300"
        }`}>
          {vital ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />} {vital ? "Vital" : "Non-Vital"}
        </button>
        <button onClick={() => setVariable(!variable)} data-testid="variable-toggle" className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors border ${
          variable ? "text-violet-400 bg-violet-500/10 border-violet-500/20" : "text-slate-500 border-slate-700 hover:text-slate-300"
        }`}>
          {variable ? "Variable" : "Fixed"}
        </button>
        <button onClick={submit} data-testid="add-expense-btn" className="ml-auto flex items-center gap-1.5 px-4 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-xs rounded-lg transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add Bill
        </button>
      </div>
    </div>
  );
}

export default function BudgetPlanner() {
  const { monthData, dispatch, totalIncome, totalExpenses, totalSavings, totalNeeds, totalWants, leftover } = useApp();
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");

  const totalOut = totalExpenses + totalSavings;
  const progress = totalIncome > 0 ? Math.min((totalOut / totalIncome) * 100, 100) : 0;
  const progressColor = progress > 100 ? "bg-rose-500" : progress > 85 ? "bg-amber-500" : "bg-emerald-500";

  const filtered = activeFilter === "all" ? monthData.expenses : monthData.expenses.filter((e) => e.category === activeFilter);

  return (
    <div className="space-y-6">
      {/* Remaining Progress Banner */}
      <div data-testid="remaining-banner" className="bg-slate-900/40 border border-white/5 rounded-xl p-5">
        <div className="flex items-end justify-between mb-3">
          <div>
            <p className="text-slate-500 text-xs mb-1">Monthly Budget Used</p>
            <div className="flex items-baseline gap-2">
              <span className="font-outfit font-bold text-white text-2xl tabular-nums">{fmt(totalOut)}</span>
              <span className="text-slate-500 text-sm">of {fmt(totalIncome)}</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-slate-500 text-xs mb-1">Remaining</p>
            <span className={`font-outfit font-bold text-xl tabular-nums ${leftover >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
              {fmt(leftover)}
            </span>
          </div>
        </div>
        <div className="w-full h-2.5 bg-slate-800 rounded-full overflow-hidden">
          <div data-testid="budget-progress" className={`h-full ${progressColor} rounded-full transition-all duration-700`} style={{ width: `${Math.min(progress, 100)}%` }} />
        </div>
        <div className="flex justify-between mt-1.5 text-[10px] text-slate-600">
          <span>0%</span>
          <span className={progress > 85 ? "text-amber-500" : ""}>{progress.toFixed(0)}% used</span>
          <span>100%</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income Section */}
        <div className="bg-slate-900/40 border border-white/5 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit font-semibold text-white">Income Sources</h2>
            <button onClick={() => setShowAddIncome(!showAddIncome)} data-testid="toggle-add-income" className="flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 font-medium transition-colors">
              <Plus className="w-3.5 h-3.5" /> Add Source
            </button>
          </div>
          {showAddIncome && <AddIncomeRow onAdd={(item) => { dispatch({ type: "ADD_INCOME", payload: item }); setShowAddIncome(false); }} />}
          <div className="space-y-2 mt-3">
            {monthData.income.length === 0 && (
              <p className="text-slate-600 text-sm text-center py-4">No income sources. Add one above.</p>
            )}
            {monthData.income.map((inc) => (
              <div key={inc.id} data-testid={`income-row-${inc.id}`} className="flex items-center justify-between bg-slate-800/40 hover:bg-slate-800/60 border border-white/5 rounded-lg px-4 py-2.5 transition-colors">
                <span className="text-slate-200 text-sm">{inc.name}</span>
                <div className="flex items-center gap-3">
                  <span className="font-outfit font-semibold text-emerald-400 tabular-nums text-sm">{fmt(inc.amount)}</span>
                  <button onClick={() => dispatch({ type: "REMOVE_INCOME", payload: inc.id })} className="text-slate-600 hover:text-rose-400 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-white/5 flex justify-between">
            <span className="text-slate-500 text-sm">Total Income</span>
            <span className="font-outfit font-bold text-emerald-400 tabular-nums">{fmt(totalIncome)}</span>
          </div>
        </div>

        {/* Bill Tracker */}
        <div className="bg-slate-900/40 border border-white/5 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-outfit font-semibold text-white">Bills & Expenses</h2>
            <button onClick={() => setShowAddExpense(!showAddExpense)} data-testid="toggle-add-expense" className="flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 font-medium transition-colors">
              <Plus className="w-3.5 h-3.5" /> Add Bill
            </button>
          </div>
          {showAddExpense && <AddExpenseRow onAdd={(item) => { dispatch({ type: "ADD_EXPENSE", payload: item }); setShowAddExpense(false); }} />}

          {/* Filter tabs */}
          <div className="flex gap-1 mt-3 mb-3">
            {["all", "needs", "wants", "savings"].map((f) => (
              <button key={f} onClick={() => setActiveFilter(f)} data-testid={`filter-${f}`} className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize transition-colors ${
                activeFilter === f ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20" : "text-slate-500 hover:text-slate-300"
              }`}>{f}</button>
            ))}
          </div>

          <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {filtered.length === 0 && <p className="text-slate-600 text-sm text-center py-4">No bills in this category.</p>}
            {filtered.map((exp) => (
              <div key={exp.id} data-testid={`expense-row-${exp.id}`} className="flex items-center gap-2 bg-slate-800/30 hover:bg-slate-800/50 border border-white/5 rounded-lg px-3 py-2 group transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-slate-200 text-xs font-medium truncate">{exp.name}</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${CAT_STYLE[exp.category]}`}>{exp.category}</span>
                    {exp.vital ? <span className="text-[10px] text-sky-400 font-medium">Vital</span> : <span className="text-[10px] text-slate-600">Non-vital</span>}
                    {exp.variable && <span className="text-[10px] text-violet-400">Variable</span>}
                  </div>
                  {exp.dueDate && <p className="text-slate-600 text-[10px] mt-0.5">Due: {exp.dueDate}th</p>}
                </div>
                <span className={`font-outfit font-semibold text-sm tabular-nums flex-shrink-0 ${
                  exp.category === "needs" ? "text-rose-400" : exp.category === "wants" ? "text-amber-400" : "text-emerald-400"
                }`}>{fmt(exp.amount)}</span>
                <button onClick={() => dispatch({ type: "REMOVE_EXPENSE", payload: exp.id })} className="opacity-0 group-hover:opacity-100 text-slate-600 hover:text-rose-400 transition-all flex-shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-3 gap-2">
            {[{ label: "Needs", v: totalNeeds, c: "text-rose-400" }, { label: "Wants", v: totalWants, c: "text-amber-400" }, { label: "Savings", v: totalSavings, c: "text-emerald-400" }].map(({ label, v, c }) => (
              <div key={label} className="text-center">
                <p className="text-slate-600 text-[10px]">{label}</p>
                <p className={`font-outfit font-bold text-sm tabular-nums ${c}`}>{fmt(v)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
